'use client';

import { useCallback, useEffect, useRef } from 'react';

import { logger } from '../../../core/Log/logger';
import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { useModal } from '../../../design-system/components/modal/useModal';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

import { DynamicExitModalNewsletter } from './ExitModalNewsletter/ExitModalNewsletter.dynamic';
import { GTM_EVENTS, USER_INTENT_EVENTS, TRACKING_ORIGINS } from './MarketingConstants';
import { DynamicMarketingOpNewsletterModal } from './MarketingOpNewsletterModal/MarketingOpNewsletterModal.dynamic';
import { DynamicThreePageViewsModalNewsletter } from './ThreePageViewsModalNewsletter/ThreePageViewsModalNewsletter.dynamic';

/**
 * Marketing Modal System
 *
 * Orchestrates modal display based on GTM events and feature flags.
 * Feature flag enabled → OP modal, disabled → specific default modals.
 */

type ModalOrigin = (typeof TRACKING_ORIGINS)[keyof typeof TRACKING_ORIGINS];

export function Marketing() {
    const { trackEvent } = useTrackEventAmplitude();
    const { hasFeature } = useFeatureFlag();

    const exitModalNewsletterProps = useModal();
    const threePageViewsModalNewsletterProps = useModal();
    const marketingOpModal = useModal();

    const modalOrigin = useRef<ModalOrigin | 'unhandled'>('unhandled');

    const trackModalAction = useCallback(
        (action: 'open' | 'close') => {
            const origin = modalOrigin.current;
            if (origin === 'unhandled') {
                return logger.error('Unhandled modal origin', {});
            }

            trackEvent((ampli, defaultProperties) => {
                if (action === 'open') {
                    ampli.popinNewsletterDisplayed({
                        ...defaultProperties,
                        origin,
                    });
                } else {
                    ampli.popinNewsletterClosed({
                        ...defaultProperties,
                        origin,
                    });
                }
            });
        },
        [trackEvent],
    );

    const handleOpenExitModal = useCallback(() => {
        if (hasFeature('marketing_op_newsletter_modal')) {
            marketingOpModal.open();
        } else {
            exitModalNewsletterProps.open();
        }
        modalOrigin.current = TRACKING_ORIGINS[USER_INTENT_EVENTS.EXIT];
        trackModalAction('open');
    }, [exitModalNewsletterProps, marketingOpModal, trackModalAction, hasFeature]);

    const handleOpenThreePageViewsModal = useCallback(() => {
        if (hasFeature('marketing_op_newsletter_modal')) {
            marketingOpModal.open();
        } else {
            threePageViewsModalNewsletterProps.open();
        }
        modalOrigin.current = TRACKING_ORIGINS[USER_INTENT_EVENTS.THREE_PAGE_VIEWS];
        trackModalAction('open');
    }, [threePageViewsModalNewsletterProps, marketingOpModal, trackModalAction, hasFeature]);

    useEffect(() => {
        document.addEventListener(GTM_EVENTS[USER_INTENT_EVENTS.EXIT], handleOpenExitModal);
        document.addEventListener(
            GTM_EVENTS[USER_INTENT_EVENTS.THREE_PAGE_VIEWS],
            handleOpenThreePageViewsModal,
        );

        return () => {
            document.removeEventListener(GTM_EVENTS[USER_INTENT_EVENTS.EXIT], handleOpenExitModal);
            document.removeEventListener(
                GTM_EVENTS[USER_INTENT_EVENTS.THREE_PAGE_VIEWS],
                handleOpenThreePageViewsModal,
            );
        };
    }, [handleOpenExitModal, handleOpenThreePageViewsModal]);

    const handleCloseOpMarketingModalNewsletter = useCallback(() => {
        trackModalAction('close');
        marketingOpModal.close();
    }, [marketingOpModal, trackModalAction]);

    const handleCloseExitModalNewsletter = useCallback(() => {
        trackModalAction('close');
        exitModalNewsletterProps.close();
    }, [exitModalNewsletterProps, trackModalAction]);

    const handleCloseThreePageViewsModalNewsletter = useCallback(() => {
        trackModalAction('close');
        threePageViewsModalNewsletterProps.close();
    }, [threePageViewsModalNewsletterProps, trackModalAction]);

    return (
        <>
            {marketingOpModal.isOpen ? (
                <DynamicMarketingOpNewsletterModal
                    {...marketingOpModal}
                    close={handleCloseOpMarketingModalNewsletter}
                />
            ) : null}

            {exitModalNewsletterProps.isOpen ? (
                <DynamicExitModalNewsletter
                    {...exitModalNewsletterProps}
                    close={handleCloseExitModalNewsletter}
                />
            ) : null}

            {threePageViewsModalNewsletterProps.isOpen ? (
                <DynamicThreePageViewsModalNewsletter
                    {...threePageViewsModalNewsletterProps}
                    close={handleCloseThreePageViewsModalNewsletter}
                />
            ) : null}
        </>
    );
}
